import React from "react";
import { useDispatch } from "react-redux";
import { trackEvent } from "src/Amplitude";
import { formatNumbersWithCommas, formatPrice } from "src/components/portfolio/helpers";
import { SanitisedTradeSummaryRes } from "src/store/features/api/generated";
import { setBuyInput, setSellInput } from "src/store/features/swap.slice";
import { useUserSlice } from "src/store/features/user.slice";

const DEFAULT_SUMMARY = {
  conversion: `Select a token to buy`,
  expectedOutput: { header: "0", fee: 0, value: 0 },
  total: "0",
  isPromotional: false,
};
interface useTradeSummaryProps {
  buyInput: Record<string, any>;
}
const useTradeSummary = (props: useTradeSummaryProps) => {
  const { buyInput } = props;
  const dispatch = useDispatch();
  const { defaultCurrency } = useUserSlice();
  const { code: userBaseCurrency } = defaultCurrency;

  const [summary, setSummary] = React.useState<Record<string, any>>(DEFAULT_SUMMARY);

  /**
   * Updates the trade summary state and dispatches actions to update the buy and sell input states.
   *
   * This function takes a summary object and updates the Trade Card component summary with the new summary data.
   * It sets the summary state with detailed information including total value, expected output, and fees.
   * Additionally, it updates the sell and buy input states with the provided units and values.
   * If the known side of the trade is "sell", it updates the sell input's approx value.
   * If the known side is "buy", it updates the buy input's approx value.
   */
  const handleSummaryChange = (summary: SanitisedTradeSummaryRes): void => {
    const approx = summary.knownSide === "sell" ? summary.buy : summary.sell;
    const feeIncrease = summary.fee?.value;
    const totalValue = summary.totalValue - feeIncrease;
    const isPromotional = summary.fee?.isPromotional ?? false;
    const partnershipCode = summary.fee?.promotionalPartnerCode ?? null;

    setSummary({
      ...summary,
      total: summary.totalValue,
      isPromotional,
      partnershipCode,
      expectedOutput: {
        header: `Approx. ${formatNumbersWithCommas(approx.units)} ${approx.currencyConfig.code}`,
        fee: summary.fee?.amount ?? 0,
        value: totalValue.toFixed(2) ?? 0,
      },
    });

    dispatch(
      setSellInput({
        value: summary.sell.units.toString(),
        units: "0",
      }),
    );
    dispatch(
      setBuyInput({
        value: summary.buy.units.toString(),
        units: "0",
      }),
    );

    if (summary.knownSide === "sell") {
      dispatch(
        setSellInput({
          approx: summary.sell.units.toString(),
        }),
      );
    } else {
      dispatch(
        setBuyInput({
          approx: summary.buy.units.toString(),
        }),
      );
    }

    trackEvent(
      "User View Trade Summary",
      {
        sellUnits: summary.sell.units,
        buyUnits: summary.buy.units,
        buyValue: summary.buy.value,
        fee: summary.fee?.amount ?? 0,
        total: summary.totalValue,
        currentBuyPrice: summary.buy.price,
      },
      "Trade",
    );
  };

  /**
   * Resets the trade summary state to the default summary.
   *
   * This function updates the trade summary state by setting it to the default summary.
   * It is used to clear the current trade summary and reset it to its initial state.
   */
  const resetSummary = () => {
    setSummary(DEFAULT_SUMMARY);
  };

  /**
   * Updates the trade summary conversion price based on the selected buy input code and price.
   *
   * This effect runs whenever the buyInput state changes. If the buy input code is not "Select",
   * it formats the buy input price into the users base currency and updates the trade summary with the conversion rate.
   */
  React.useEffect(() => {
    if (buyInput.code !== "Select") {
      const conversionPrice = formatPrice(buyInput.price, userBaseCurrency);
      setSummary({ ...summary, conversion: `1 ${buyInput.code} = ${conversionPrice}` });
    }
  }, [buyInput, userBaseCurrency]);

  return { summary, handleSummaryChange, resetSummary };
};

export default useTradeSummary;
